import React from 'react';
import { Zap, Clock, AlertCircle } from 'lucide-react';

const RJHComparison = () => {
  const comparisonData = [
    {
      aspect: 'Processing time',
      rjh: 'Seconds-scale treatment',
      furnace: 'Typically minutes to hours'
    },
    {
      aspect: 'Heating / cooling rate',
      rjh: 'Ultrafast heating & cooling',
      furnace: 'Slower thermal ramping'
    },
    {
      aspect: 'Temperature capability',
      rjh: 'Ultra-high temperature regimes',
      furnace: 'High, but limited by longer dwell requirements'
    },
    {
      aspect: 'Material conditions',
      rjh: 'Non-equilibrium transformation possible',
      furnace: 'Closer to equilibrium processing'
    },
    {
      aspect: 'Energy utilization',
      rjh: 'High Energy Efficiency Utilization (targeted heating)',
      furnace: 'Lower efficiency due to bulk heating over time'
    },
    {
      aspect: 'Scalability & sustainability',
      rjh: 'Green, scalable pathway',
      furnace: 'Scalable but energy/time intensive'
    },
    {
      aspect: 'Best for',
      rjh: 'Rapid screening, advanced carbon structures, fast turnaround',
      furnace: 'Long-dwell processing, conventional carbonisation'
    }
  ];

  return (
    <section id="rjh-vs-furnace" className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Why Rapid Joule Heating (RJH)?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
            RJH enables ultrafast, high-temperature treatment under non-equilibrium conditions—unlocking material structures
            that conventional furnace processing may not achieve within practical time and energy limits.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto rounded-2xl border-2 border-gray-200 shadow-lg mb-8">
          <table className="w-full border-collapse min-w-[760px]">
            <thead>
              <tr className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
                <th className="text-left p-4 font-semibold text-base border-r border-white/20">
                  Comparison
                </th>
                <th className="text-left p-4 font-semibold text-base border-r border-white/20">
                  <div className="flex items-center space-x-2">
                    <Zap className="w-5 h-5" />
                    <span>Rapid Joule Heating (RJH)</span>
                  </div>
                </th>
                <th className="text-left p-4 font-semibold text-base">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>Conventional Furnace</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {comparisonData.map((row, index) => (
                <tr 
                  key={index} 
                  className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-green-50 transition-colors duration-200`}
                >
                  <td className="p-4 border-t border-gray-200 font-semibold text-gray-900">
                    {row.aspect}
                  </td>
                  <td className="p-4 border-t border-l border-gray-200 text-gray-700">
                    {row.rjh}
                  </td>
                  <td className="p-4 border-t border-l border-gray-200 text-gray-700">
                    {row.furnace}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Info Box */}
        <div className="bg-orange-50 border-l-4 border-orange-600 rounded-lg p-6 flex items-start space-x-4">
          <div className="text-3xl">🔥</div>
          <div>
            <p className="text-base text-gray-800 leading-relaxed font-medium">
              <strong className="font-bold">Why this matters for you:</strong> RJH helps you test multiple synthesis conditions quickly, shorten R&D cycles,
              and obtain research-ready carbon materials faster—without waiting for long furnace runs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RJHComparison;
